import React, { useState, useEffect } from 'react';
import { useParams, Redirect, Link } from 'react-router-dom';
import { invites, setCsrfToken } from './api';
import { useAuth, homeForRole } from './AuthContext';
import './Portal.css';

/**
 * Landing page for the one-time link in the owner's invitation email. The
 * server signs the new account in as part of accepting, so there is no second
 * "now please log in" step.
 */
export default function AcceptInvite() {
  const { token } = useParams();
  const { adoptSession } = useAuth();

  const [state, setState] = useState('checking');
  const [invite, setInvite] = useState(null);
  const [problem, setProblem] = useState(null);

  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    let active = true;
    invites
      .verify(token)
      .then((result) => {
        if (!active) return;
        if (result.valid) {
          setInvite(result);
          setName(result.name === 'Owner' ? '' : result.name);
          setState('valid');
        } else {
          setProblem(result.message);
          setState('invalid');
        }
      })
      .catch((err) => {
        if (!active) return;
        setProblem(err.message);
        setState('invalid');
      });
    return () => {
      active = false;
    };
  }, [token]);

  if (done) {
    return <Redirect to={homeForRole(done.role)} />;
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      const result = await invites.accept(token, name.trim(), password);
      setCsrfToken(result.csrf_token);
      adoptSession(result.user, result.csrf_token);
      setDone(result.user);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  return (
    <div className="pt-app pt-auth">
      <div className="pt-auth-card">
        <p className="pt-auth-eyebrow">Northwoods Handy</p>

        {state === 'checking' && (
          <>
            <h1 className="pt-auth-title">Checking your invitation…</h1>
            <p className="pt-auth-sub">One moment.</p>
          </>
        )}

        {state === 'invalid' && (
          <>
            <h1 className="pt-auth-title">This link will not work</h1>
            <div className="pt-notice pt-notice-error">{problem}</div>
            <div className="pt-auth-foot">
              <Link to="/portal">Go to sign in</Link>
              <br />
              Need a new invitation? Email{' '}
              <a href="mailto:eugenioespinoza57@yahoo.com">eugenioespinoza57@yahoo.com</a>
            </div>
          </>
        )}

        {state === 'valid' && (
          <>
            <h1 className="pt-auth-title">Set up your account</h1>
            <p className="pt-auth-sub">
              You are setting up the portal for <strong>{invite.email}</strong>. Choose a password and you
              will be signed straight in.
            </p>

            <form className="pt-form" onSubmit={handleSubmit}>
              <div className="pt-field">
                <label className="pt-label" htmlFor="pt-invite-name-field">
                  Your name
                </label>
                <input
                  id="pt-invite-name-field"
                  className="pt-input"
                  type="text"
                  value={name}
                  placeholder="How should we greet you?"
                  onChange={(e) => setName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="pt-field">
                <label className="pt-label" htmlFor="pt-invite-password">
                  Choose a password
                </label>
                <input
                  id="pt-invite-password"
                  className="pt-input"
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
                <span className="pt-hint">At least 10 characters, including a letter and a number.</span>
              </div>

              <div className="pt-field">
                <label className="pt-label" htmlFor="pt-invite-confirm">
                  Confirm password
                </label>
                <input
                  id="pt-invite-confirm"
                  className="pt-input"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
              </div>

              {error && <div className="pt-notice pt-notice-error">{error}</div>}

              <button className="pt-btn pt-btn-primary pt-btn-block" type="submit" disabled={busy}>
                {busy && <span className="pt-spinner" />}
                {busy ? 'Creating your account…' : 'Create my account'}
              </button>
            </form>

            <div className="pt-auth-foot">Nobody else ever sees this password, including your webmaster.</div>
          </>
        )}
      </div>
    </div>
  );
}
